import React from "react";

// region imports - External libraries
import _ from "lodash";
// endregion imports - External libraries
// region imports - Atoms
import AccordionSummaryChips from "@atoms/AccordionSummaryChips";
// endregion imports - Atoms
// region imports - Molecules
import KanbanAccordionCard from "@molecules/KanbanAccordionCard";
// endregion imports - Molecules
// region imports - Organisms
import { IDateRangeQuickRange, IDateRangeValues, MAX_RANGE_IN_DAYS, MaxRangeType } from "@organisms/GenericQueryFilter";
// endregion imports - Organisms
// region imports - Languages
import { FilterMessages, YupMessages } from "@shared/languages/interfaces";
import useTranslation from "src/translations/useTranslation";
// endregion imports - Languages
// region imports - Utils
import * as dateFns from "date-fns";
import DateRange from "@shared/utils/date-range";
import utils from "@utils/useful-functions";
// endregion imports - Utils
// region imports - Libraries
import { TextField } from "@libraries/mui";
// endregion imports - Libraries
// region imports - Styles
import * as Styled from "./styles";
// endregion imports - Styles

/** Helper text for each limit. Pairs with MAX_RANGE_IN_DAYS — a new option has to appear in both. */
const MAX_RANGE_MESSAGE: Record<MaxRangeType, string> = {
  "1D": YupMessages.maxDateInterval24h,
  "7D": YupMessages.maxDateInterval7d,
  "30D": YupMessages.maxDateInterval30d,
  "60D": YupMessages.maxDateInterval60d
};

// region Interfaces

interface IProps {
  label: string;
  onChangeDateRange?: (values: IDateRangeValues) => void;
  defaultValues: IDateRangeValues;
  maxRange?: MaxRangeType;
  maxPeriod?: MaxRangeType;
  /** Optional shortcuts shown above the fields; without them the card is exactly what it always was */
  quickRanges?: IDateRangeQuickRange[];
}

// endregion Interfaces

const KanbanAccordionCardFilterDateRange: React.FC<IProps> = ({
  label,
  onChangeDateRange,
  defaultValues,
  maxRange,
  maxPeriod,
  quickRanges
}) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region Refs
  const lastDefaultValues = React.useRef<IDateRangeValues>(defaultValues);
  // endregion Refs
  // region States
  const [expandedCard, setExpandedCard] = React.useState<boolean>(false);

  const [currentStartDate, setCurrentStartDate] = React.useState<string>(defaultValues.startDate);
  const [currentEndDate, setCurrentEndDate] = React.useState<string>(defaultValues.endDate);

  const [startDateHelperMessage, setStartDateHelperMessage] = React.useState<string>("");
  const [endDateHelperMessage, setEndDateHelperMessage] = React.useState<string>("");
  // endregion States
  // region Functions
  /**
   * Function to handle the change of the accordion card (Expand or collapse)
   * @param _event The event
   * @param isExpanded If the card is expanded or not
   */
  const handleAccordionCardChange = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCard(isExpanded);
  };

  /**
   * Function to handle the change of the date fields
   * @param event The event
   */
  const handleChangeDate = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = event.target;

    if (!value) return;

    validateRange(name, value);

    let startDate: Date;
    let endDate: Date;
    let setCurrentDateField: React.Dispatch<React.SetStateAction<string>>;

    if (name === "startDate") {
      startDate = new Date(value);
      endDate = new Date(currentEndDate);
      setCurrentDateField = setCurrentStartDate;
    } else {
      startDate = new Date(currentStartDate);
      endDate = new Date(value);
      setCurrentDateField = setCurrentEndDate;
    }

    setCurrentDateField(value);

    const formattedStartDate = utils.convertDateToISOWithTimezone(startDate);
    const formattedEndDate = utils.convertDateToISOWithTimezone(endDate);

    if (onChangeDateRange) onChangeDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
  };

  /**
   * Function to handle a click on one of the shortcuts
   *
   * The range is resolved on the click, by whoever offered it, so no rule about which days a screen
   * considers valid ever reaches this card. A shortcut is well ordered by construction, so the
   * helper messages are simply cleared; a range longer than `maxRange`/`maxPeriod` is still caught
   * by the caller of `onChangeDateRange`, which disables the apply button.
   * @param quickRange The shortcut the user clicked
   */
  const handleSelectQuickRange = (quickRange: IDateRangeQuickRange) => {
    const values = quickRange.resolveValues();

    setStartDateHelperMessage("");
    setEndDateHelperMessage("");
    setCurrentStartDate(values.startDate);
    setCurrentEndDate(values.endDate);

    if (onChangeDateRange) onChangeDateRange(values);
  };

  /**
   * Check if a shortcut is the range currently in the fields
   *
   * The fields only carry minutes, so anything finer than a minute would never match back.
   * @param quickRange The shortcut to check
   */
  const isSelectedQuickRange = (quickRange: IDateRangeQuickRange): boolean => {
    const values = quickRange.resolveValues();

    return values.startDate.slice(0, 16) === currentStartDate.slice(0, 16)
      && values.endDate.slice(0, 16) === currentEndDate.slice(0, 16);
  };

  /**
   * Validate the range of dates and set the yup message if necessary
   * @param name The name of the field
   * @param value The value of the field
   */
  const validateRange = (name: string, value: string) => {
    let startDate: string;
    let endDate: string;
    let setYupMessageField: React.Dispatch<React.SetStateAction<string>>;

    if (name === "startDate") {
      setYupMessageField = setStartDateHelperMessage;
      startDate = value;
      endDate = currentEndDate;
    } else {
      setYupMessageField = setEndDateHelperMessage;
      startDate = currentStartDate;
      endDate = value;
    }

    if (startDate > endDate) {
      setYupMessageField(t(YupMessages.finishDateGreaterThanStartDate));

      return;
    }

    if (maxPeriod) {
      const differenceInDays = dateFns.differenceInCalendarDays(new Date(), new Date(startDate));

      let maxPeriodInDays: number;
      let maxPeriodDateYupMessage: string;

      if (maxPeriod === "30D") {
        maxPeriodInDays = 30;
        maxPeriodDateYupMessage = YupMessages.maxPeriodInterval30d;
      } else if (maxPeriod === "7D") {
        maxPeriodInDays = 7;
        maxPeriodDateYupMessage = YupMessages.maxPeriodInterval7d;
      } else {
        maxPeriodInDays = 1;
        maxPeriodDateYupMessage = YupMessages.maxPeriodInterval30d;
      }
      if (differenceInDays > maxPeriodInDays) {
        setYupMessageField(t(maxPeriodDateYupMessage));

        return;
      }
    }

    if (maxRange) {
      const dateRange = new DateRange(startDate, endDate);

      const maxRangeInDays = MAX_RANGE_IN_DAYS[maxRange];
      const maxDateYupMessage = MAX_RANGE_MESSAGE[maxRange];

      if (!dateRange.isValidRange("DAYS", maxRangeInDays)) {
        setYupMessageField(t(maxDateYupMessage));

        return;
      }
    }

    setStartDateHelperMessage("");
    setEndDateHelperMessage("");
  };

  // endregion Functions
  // region Effects
  // UseEffect to update the current date values when the default values change
  React.useEffect(() => {
    if (_.isEqual(defaultValues, lastDefaultValues.current)) return;

    setCurrentStartDate(defaultValues.startDate);
    setCurrentEndDate(defaultValues.endDate);
    lastDefaultValues.current = defaultValues;
  }, [defaultValues]);
  // endregion Effects

  return (
    <Styled.Container>
      <KanbanAccordionCard
        onChange={handleAccordionCardChange}
        showExpandIcon
        accordionSummary={(
          <AccordionSummaryChips
            label={label}
            chips={[]}
            onDeleteChip={() => undefined}
          />
        )}
        expanded={expandedCard}
      >
        <Styled.AccordionDetail>
          { expandedCard && (
            <>
              {!!quickRanges?.length && (
                <Styled.QuickRanges>
                  {quickRanges.map((quickRange) => (
                    <button
                      key={quickRange.label}
                      type="button"
                      aria-pressed={isSelectedQuickRange(quickRange)}
                      onClick={() => handleSelectQuickRange(quickRange)}
                    >
                      {quickRange.label}
                    </button>
                  ))}
                </Styled.QuickRanges>
              )}
              <TextField
                name="startDate"
                label={t(FilterMessages.optionStartDate)}
                fullWidth
                type="datetime-local"
                value={currentStartDate.slice(0, 16)}
                onChange={(event) => handleChangeDate(event)}
                InputLabelProps={{ shrink: true }}
                error={!_.isEmpty(startDateHelperMessage)}
                helperText={startDateHelperMessage}
              />
              <TextField
                name="endDate"
                label={t(FilterMessages.optionEndDate)}
                fullWidth
                type="datetime-local"
                onChange={(event) => handleChangeDate(event)}
                value={currentEndDate.slice(0, 16)}
                InputLabelProps={{ shrink: true }}
                error={!_.isEmpty(endDateHelperMessage)}
                helperText={endDateHelperMessage}
              />
            </>
          )}
        </Styled.AccordionDetail>
      </KanbanAccordionCard>
    </Styled.Container>
  );
};

KanbanAccordionCardFilterDateRange.defaultProps = {
  onChangeDateRange: () => undefined,
  maxRange: undefined,
  maxPeriod: undefined,
  quickRanges: undefined
};

export default KanbanAccordionCardFilterDateRange;
